/**
 * MovieVibeField Component
 * Interactive 2D plane for Pacing/Tone + sliders for Obscurity/Formalism
 */

import { useRef, useState } from 'react';
import { useMovieExploreStore } from '../../stores/movie-explore-store';

export default function MovieVibeField() {
  const { vibe, setVibe } = useMovieExploreStore();
  const planeRef = useRef<SVGSVGElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  /**
   * Convert SVG coordinates to 0-1 normalized values
   */
  const coordsToVibe = (clientX: number, clientY: number) => {
    if (!planeRef.current) return { pacing: 0.5, tone: 0.5 };

    const rect = planeRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height)); // Invert Y

    return { pacing: x, tone: y };
  };

  const handlePlaneMove = (clientX: number, clientY: number) => {
    const { pacing, tone } = coordsToVibe(clientX, clientY);
    setVibe({ pacing, tone });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    handlePlaneMove(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      handlePlaneMove(e.clientX, e.clientY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    handlePlaneMove(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging) {
      const touch = e.touches[0];
      handlePlaneMove(touch.clientX, touch.clientY);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Convert vibe values to SVG coordinates (0-1 to 0-300)
  const pointX = vibe.pacing * 300;
  const pointY = (1 - vibe.tone) * 300; // Invert Y axis

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Vibe Field</h2>

      {/* 2D Plane (Pacing × Tone) */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Pacing × Tone</span>
          <span className="text-xs text-gray-500">
            ({vibe.pacing.toFixed(2)}, {vibe.tone.toFixed(2)})
          </span>
        </div>

        <svg
          ref={planeRef}
          viewBox="0 0 300 300"
          className="w-full h-auto border-2 border-gray-300 rounded-lg cursor-crosshair select-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Background gradient */}
          <defs>
            <linearGradient id="movieToneGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#FDE68A" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#6B7280" stopOpacity="0.4" />
            </linearGradient>
            <linearGradient id="moviePacingGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#E5E7EB" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.3" />
            </linearGradient>
          </defs>

          {/* Background */}
          <rect width="300" height="300" fill="url(#movieToneGradient)" />
          <rect width="300" height="300" fill="url(#moviePacingGradient)" opacity="0.5" />

          {/* Grid lines */}
          {[0, 75, 150, 225, 300].map((pos) => (
            <g key={pos}>
              <line
                x1={pos} y1={0} x2={pos} y2={300}
                stroke="#D1D5DB" strokeWidth="1" strokeDasharray="4"
              />
              <line
                x1={0} y1={pos} x2={300} y2={pos}
                stroke="#D1D5DB" strokeWidth="1" strokeDasharray="4"
              />
            </g>
          ))}

          {/* Axis labels */}
          <text x="150" y="15" fontSize="11" fill="#6B7280" textAnchor="middle">
            Warm
          </text>
          <text x="150" y="295" fontSize="11" fill="#6B7280" textAnchor="middle">
            Bleak
          </text>
          <text x="8" y="155" fontSize="11" fill="#6B7280" textAnchor="start">
            Slow Burn
          </text>
          <text x="292" y="155" fontSize="11" fill="#6B7280" textAnchor="end">
            Kinetic
          </text>

          {/* Position point */}
          <circle
            cx={pointX}
            cy={pointY}
            r="12"
            fill="#7C3AED"
            stroke="#FFFFFF"
            strokeWidth="3"
            className="transition-all duration-100"
          />
          <circle cx={pointX} cy={pointY} r="4" fill="#FFFFFF" />
        </svg>
      </div>

      {/* Obscurity Slider */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700">Obscurity</label>
          <span className="text-xs text-gray-500">{vibe.obscurity.toFixed(2)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Mainstream</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={vibe.obscurity}
            onChange={(e) => setVibe({ obscurity: parseFloat(e.target.value) })}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
          />
          <span className="text-xs text-gray-500">Rare</span>
        </div>
      </div>

      {/* Formalism Slider */}
      <div className="mb-2">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700">Formalism</label>
          <span className="text-xs text-gray-500">{vibe.formalism.toFixed(2)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Raw</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={vibe.formalism}
            onChange={(e) => setVibe({ formalism: parseFloat(e.target.value) })}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
          />
          <span className="text-xs text-gray-500">Stylized</span>
        </div>
      </div>
    </div>
  );
}
